"use client";

import { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { isLexicalEmpty } from "../../../lib/lexical-utils";
import { ExpandableText } from "../ExpandableText";

interface RichDescriptionProps {
  content: unknown;
  fallbackText: string | null;
  /** Brand accent color for the read-more / show-less toggle. */
  accentColor?: string;
  /** Fired when user expands; parent uses this to grow the bottom sheet snap. */
  onExpand?: () => void;
}

interface LexicalNode {
  type: string;
  text?: string;
  format?: number;
  url?: string;
  children?: LexicalNode[];
}

interface LexicalRoot {
  root?: LexicalNode;
}

/**
 * Convert Lexical text format bitmask to HTML tags
 * 1 = bold, 2 = italic, 4 = underline
 */
function applyFormat(text: string, format: number): string {
  let result = text;
  if (format & 4) result = `<u>${result}</u>`;
  if (format & 2) result = `<em>${result}</em>`;
  if (format & 1) result = `<strong>${result}</strong>`;
  return result;
}

/**
 * Recursively convert Lexical JSON nodes to HTML
 */
function lexicalNodeToHtml(node: LexicalNode): string {
  if (node.type === "text" && node.text) {
    const escaped = node.text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return applyFormat(escaped, node.format || 0);
  }

  if (node.type === "link" && node.url) {
    const childHtml = node.children?.map(lexicalNodeToHtml).join("") || "";
    const isExternal = !node.url.startsWith("/");
    if (isExternal) {
      return `<a href="${node.url}" target="_blank" rel="noopener noreferrer">${childHtml}</a>`;
    }
    return `<a href="${node.url}">${childHtml}</a>`;
  }

  if (node.type === "paragraph") {
    const childHtml = node.children?.map(lexicalNodeToHtml).join("") || "";
    return `<p>${childHtml || "&nbsp;"}</p>`;
  }

  if (node.type === "list") {
    const childHtml = node.children?.map(lexicalNodeToHtml).join("") || "";
    // Check if it's numbered (ordered) or bulleted
    // Lexical uses listType: "number" for ordered, "bullet" for unordered
    // Also check tag property as fallback
    const nodeWithList = node as LexicalNode & { listType?: string; tag?: string };
    const isOrdered = nodeWithList.listType === "number" || nodeWithList.tag === "ol";
    const listTag = isOrdered ? "ol" : "ul";
    return `<${listTag}>${childHtml}</${listTag}>`;
  }

  if (node.type === "listitem") {
    // List items in Lexical may contain nested elements (paragraph, text, etc.)
    const childHtml = node.children?.map(lexicalNodeToHtml).join("") || "";
    // Strip wrapping <p> tags from list item content for cleaner output
    const cleanedHtml = childHtml.replace(/^<p>(.*)<\/p>$/s, "$1");
    return `<li>${cleanedHtml}</li>`;
  }

  if (node.type === "root") {
    return node.children?.map(lexicalNodeToHtml).join("") || "";
  }

  // Unknown node type - try to render children
  if (node.children) {
    return node.children.map(lexicalNodeToHtml).join("");
  }

  return "";
}

/**
 * Convert Lexical JSON to sanitized HTML
 */
function lexicalToHtml(json: unknown): string {
  if (!json || typeof json !== "object") return "";

  const root = (json as LexicalRoot).root;
  if (!root) return "";

  const rawHtml = lexicalNodeToHtml(root);
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ["p", "strong", "em", "u", "ul", "ol", "li", "a"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
}

/**
 * Visitor component for displaying rich POI descriptions.
 * Renders Lexical JSON as sanitized HTML, with fallback to plain text.
 * Returns null if no content.
 */
export function RichDescription({ content, fallbackText, accentColor, onExpand }: RichDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  const html = useMemo(() => {
    // Try rich content first
    if (content && !isLexicalEmpty(content)) {
      return lexicalToHtml(content);
    }

    // Fallback to plain text
    if (fallbackText) {
      const escaped = fallbackText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      // Convert newlines to paragraphs
      const paragraphs = escaped.split("\n").filter(Boolean);
      if (paragraphs.length === 0) return "";
      return paragraphs.map((p) => `<p>${p}</p>`).join("");
    }

    return "";
  }, [content, fallbackText]);

  if (!html) return null;

  const interactive = canExpand;
  const toggle = () => setExpanded((v) => !v);

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={
        interactive
          ? (e) => {
              if ((e.target as HTMLElement).closest("a,button")) return;
              toggle();
            }
          : undefined
      }
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
              }
            }
          : undefined
      }
      className={interactive ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded" : undefined}
    >
      <ExpandableText
        html={html}
        lines={3}
        textClassName="prose prose-sm max-w-none text-muted-foreground [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_li]:my-1"
        expanded={expanded}
        onExpandedChange={setExpanded}
        onCanExpandChange={setCanExpand}
        onExpand={onExpand}
        accentColor={accentColor}
      />
    </div>
  );
}
